import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readAppEnv } from '../../lib/env';
import { useAuth } from '../auth/AuthProvider';
import { ensureProjectFolder, uploadFileToDrive, withDriveToken } from './drive';
import {
  deleteFile,
  FILE_BROWSER_PAGE_SIZE,
  fetchFileBrowserPage,
  fetchFileCounts,
  fetchTaskFiles,
  insertFileRecord,
  updateFile,
} from './queries';
import { parseDriveLink } from './types';
import type { AttachLinkInput, FileBrowserFilters, UploadFileInput } from './types';
import type { FileUpdate } from './queries';

/**
 * Server state for files. The thread of work in an upload: Google token →
 * project folder → Drive upload → metadata row. Each step can refuse; the
 * caller gets one error it can explain.
 */

export const fileKeys = {
  all: ['files'] as const,
  task: (taskId: string) => [...fileKeys.all, 'task', taskId] as const,
  counts: (projectId: string) => [...fileKeys.all, 'counts', projectId] as const,
  browser: (filters: FileBrowserFilters) => [...fileKeys.all, 'browser', filters] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

/** Cache identity: project-level attachments (taskId '') key by project. */
function fileScopeId(taskId: string, projectId: string): string {
  return taskId === '' ? `project:${projectId}` : taskId;
}

export function useTaskFiles(taskId: string | undefined, projectId = '') {
  const { client } = useAuth();
  return useQuery({
    queryKey: fileKeys.task(fileScopeId(taskId ?? '', projectId)),
    queryFn: () => fetchTaskFiles(client, taskId as string, projectId),
    enabled: taskId === '' ? projectId !== '' : Boolean(taskId),
    retry: false,
  });
}

export function useFileCounts(projectId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: fileKeys.counts(projectId ?? ''),
    queryFn: () => fetchFileCounts(client, projectId as string),
    enabled: Boolean(projectId),
    retry: false,
  });
}

/**
 * The cross-project Files browser: pages of latest versions, newest first.
 * A full page means there may be more; a short page is the end.
 */
export function useFileBrowser(filters: FileBrowserFilters) {
  const { client } = useAuth();
  return useInfiniteQuery({
    queryKey: fileKeys.browser(filters),
    queryFn: ({ pageParam }) => fetchFileBrowserPage(client, filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === FILE_BROWSER_PAGE_SIZE ? allPages.length * FILE_BROWSER_PAGE_SIZE : null,
    retry: false,
  });
}

/**
 * Every brand-new record starts its own version chain (a group of one, which
 * the database numbers V1). The chain is what later versions attach to, and
 * asset_group_id is frozen after insert — so it must exist from the start.
 */
function newAssetGroupId(): string {
  return crypto.randomUUID();
}

/**
 * Attach a pasted link. A Google Drive link is recognised and recorded as
 * one (unlocking the Drive preview); anything else is an external link.
 */
export function useAttachLink(taskId: string, projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = useActorId();
  return useMutation({
    mutationFn: (input: Omit<AttachLinkInput, 'taskId' | 'projectId'>) => {
      const driveFileId = parseDriveLink(input.url);
      return insertFileRecord(client, {
        taskId,
        projectId,
        source: driveFileId === null ? 'external_link' : 'drive_link',
        driveFileId,
        url: input.url.trim(),
        name: input.name,
        mimeType: null,
        sizeBytes: null,
        category: input.category,
        uploadedBy,
        assetGroupId: newAssetGroupId(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: fileKeys.task(fileScopeId(taskId, projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: fileKeys.counts(projectId) });
    },
  });
}

/**
 * Upload a file (or a new version of one, when assetGroupId is set):
 * browser → Drive → metadata row. projectName names the Drive subfolder.
 */
export function useUploadFile(taskId: string, projectId: string, projectName: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = useActorId();
  return useMutation({
    mutationFn: async (input: Omit<UploadFileInput, 'taskId' | 'projectId'>) => {
      const rootFolderId = readAppEnv().driveRootFolderId;
      if (rootFolderId === null) {
        throw new Error('drive_not_configured');
      }
      const uploaded = await withDriveToken(client, async (token) => {
        const folderId = await ensureProjectFolder(token, rootFolderId, projectName);
        return uploadFileToDrive(token, folderId, input.file);
      });

      await insertFileRecord(client, {
        taskId,
        projectId,
        source: 'drive_upload',
        driveFileId: uploaded.id,
        url: uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`,
        name: uploaded.name,
        mimeType: uploaded.mimeType,
        sizeBytes: uploaded.size === undefined ? null : Number(uploaded.size),
        category: input.category,
        uploadedBy,
        // Joining an existing chain makes this the next version; otherwise a
        // fresh chain begins so future versions have something to join.
        assetGroupId: input.assetGroupId ?? newAssetGroupId(),
        ...(input.versionNote === undefined ? {} : { versionNote: input.versionNote }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: fileKeys.task(fileScopeId(taskId, projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: fileKeys.counts(projectId) });
    },
  });
}

export function useUpdateFile(taskId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, changes }: { fileId: string; changes: FileUpdate }) =>
      updateFile(client, fileId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileKeys.task(taskId) });
    },
  });
}

export function useDeleteFile(taskId: string, projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => deleteFile(client, fileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: fileKeys.task(fileScopeId(taskId, projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: fileKeys.counts(projectId) });
    },
  });
}
